import { __rest } from 'tslib';
import { jsx } from 'react/jsx-runtime';
import React__default, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import MuiTooltip from '@mui/material/Tooltip';
import { ClickAwayListener } from '@mui/material';
import { noop } from 'lodash';
import { useHintDuration } from '../HintProvider/HintProvider.js';

const Hint = (_a) => {
    var _b, _c, _d, _e, _f, _g;
    var { children, title = '', arrow, disabled, duration, clickable, preventCloseHintOnTargetClick, forceShowHint = false, isInteractiveHint } = _a, rest = __rest(_a, ["children", "title", "arrow", "disabled", "duration", "clickable", "preventCloseHintOnTargetClick", "forceShowHint", "isInteractiveHint"]);
    const [isVisible, setIsVisible] = useState(false);
    const visibleRef = useRef(false);
    const timeoutRef = useRef(null);
    useEffect(() => {
        if (disabled) {
            setIsVisible(false);
            visibleRef.current = false;
        }
    }, [disabled]);
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);
    const { enterDelay, leaveDelay } = rest;
    const selectedDuration = useHintDuration({ duration, enterDelay, leaveDelay, clickable });
    const showHint = useCallback(() => {
        if (disabled || isInteractiveHint)
            return;
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        if (!selectedDuration.enter) {
            setIsVisible(true);
            visibleRef.current = true;
            return;
        }
        timeoutRef.current = setTimeout(() => {
            setIsVisible(true);
            visibleRef.current = true;
        }, selectedDuration.enter);
    }, [disabled, isInteractiveHint, selectedDuration]);
    const hideHint = useCallback(() => {
        if (disabled || isInteractiveHint)
            return;
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        if (!selectedDuration.leave) {
            setIsVisible(false);
            visibleRef.current = false;
            return;
        }
        timeoutRef.current = setTimeout(() => {
            setIsVisible(false);
            visibleRef.current = false;
        }, selectedDuration.leave);
    }, [disabled, isInteractiveHint, selectedDuration]);
    const target = useMemo(() => {
        var _a;
        if (clickable && !isInteractiveHint) {
            const onClick = (_a = children.props) === null || _a === void 0 ? void 0 : _a.onClick;
            return React__default.cloneElement(children, {
                onClick: (...args) => {
                    if (!preventCloseHintOnTargetClick) {
                        if (visibleRef.current) {
                            hideHint();
                        }
                        else {
                            showHint();
                        }
                    }
                    onClick(args);
                },
            });
        }
        return children;
    }, [clickable, isInteractiveHint, children, preventCloseHintOnTargetClick, hideHint, showHint]);
    const props = isInteractiveHint
        ? {
            enterDelay: selectedDuration.enter,
            leaveDelay: selectedDuration.leave,
        }
        : {
            open: isVisible || forceShowHint,
            onMouseEnter: clickable ? undefined : showHint,
            onMouseMoveCapture: clickable ? undefined : showHint,
            onMouseLeave: clickable ? undefined : hideHint,
            onMouseDown: clickable || preventCloseHintOnTargetClick ? undefined : hideHint,
        };
    return (jsx(ClickAwayListener, Object.assign({ onClickAway: clickable ? hideHint : noop }, { children: jsx(MuiTooltip, Object.assign({ title: title || '' }, rest, { componentsProps: Object.assign(Object.assign({}, ((_b = rest.componentsProps) !== null && _b !== void 0 ? _b : {})), { popper: Object.assign(Object.assign({}, ((_d = (_c = rest.componentsProps) === null || _c === void 0 ? void 0 : _c.popper) !== null && _d !== void 0 ? _d : {})), { style: Object.assign(Object.assign({}, ((_g = (_f = (_e = rest.componentsProps) === null || _e === void 0 ? void 0 : _e.popper) === null || _f === void 0 ? void 0 : _f.style) !== null && _g !== void 0 ? _g : {})), { zIndex: 100002 }) }) }), arrow: arrow, followCursor: !arrow && !forceShowHint }, props, { children: target })) })));
};

export { Hint as default };
//# sourceMappingURL=Hint.js.map
